import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  signal
} from '@angular/core';
import { ChartConfiguration, ChartData, ChartDataset, Plugin, TooltipItem } from 'chart.js';
import * as moment from 'moment';
import { forkJoin, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { IArticleVariant } from '../../../core/interfaces';
import { NotificationService } from '../../../core/services';
import { ArticlesService } from '../../../pages/articles/core/ArticlesService';
import { IArticleSalesForecastOptions } from '../../../pages/articles/core/interfaces/IArticleSalesForecastOptions';
import { IArticleSalesForecastRun } from '../../../pages/articles/core/interfaces/IArticleSalesForecastRun';
import { IArticleVariantSalesForecast } from '../../../pages/articles/core/interfaces/IArticleVariantSalesForecast';
import { ISalesForecastMonth } from '../../../pages/articles/core/interfaces/ISalesForecastMonth';

type MhdTagKind = 'ok' | 'soon' | 'risk' | 'none';

@Component({
  selector: 'app-article-sales-forecast',
  templateUrl: './article-sales-forecast.component.html',
  styleUrl: './article-sales-forecast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ArticleSalesForecastComponent implements OnDestroy {
  @Input() set articleId(val: number) {
    if (!val || val === this._articleId) {
      return;
    }

    this._articleId = val;
    this.stopPolling();
    this.currentRun.set(null);
    this.loadInitial();
  }

  @Input() articleVariants: IArticleVariant[] = [];
  @Input() hasVariants = false;

  public readonly isLoading = signal(false);
  public readonly isCalculating = signal(false);
  public readonly currentRun = signal<IArticleSalesForecastRun | null>(null);

  private _pendingRun: IArticleSalesForecastRun | null = null;

  public options: IArticleSalesForecastOptions | null = null;
  public selectedPeriodMonths = 12;
  public selectedStrategy = 'Arima';
public groupVariants = false;
public selectedChartVariantId: number | 'aggregated' | null = null;
  public periodChoices: number[] = [6, 12, 24];

  public chartData: ChartData<'line'> = { labels: [], datasets: [] };
  public readonly chartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => this.formatTooltipLabel(ctx)
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Verkauf / Prognose' },
        beginAtZero: true
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Bestand' },
        beginAtZero: true,
        grid: { drawOnChartArea: false }
      }
    }
  };

  public readonly heutePlugin: Plugin<'line'> = {
    id: 'heuteLine',
    afterDatasetsDraw: (chart) => {
      // "heute" = first forecast-only month (current calendar month), not the green continuity bridge on last Ist.
      const historyData = (chart.data.datasets?.[0]?.data ?? []) as (number | null)[];
      const forecastData = (chart.data.datasets?.[3]?.data ?? []) as (number | null)[];
      let heuteIndex = -1;
      for (let i = 0; i < forecastData.length; i++) {
        if (forecastData[i] != null && historyData[i] == null) {
          heuteIndex = i;
          break;
        }
      }
      if (heuteIndex < 0) {
        for (let i = historyData.length - 1; i >= 0; i--) {
          if (historyData[i] != null) {
            heuteIndex = i;
            break;
          }
        }
      }
      if (heuteIndex < 0) {
        return;
      }

      const xScale = chart.scales['x'];
      if (!xScale) {
        return;
      }

      const x = xScale.getPixelForValue(heuteIndex);
      const { ctx, chartArea } = chart;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = 'rgba(0,0,0,0.45)';
      ctx.lineWidth = 1;
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('heute', x + 4, chartArea.top + 12);
      ctx.restore();
    }
  };

  private _articleId: number;
  private readonly _destroy$ = new Subject<void>();
  private _pollSub: Subscription | null = null;
  /** Raw monthly rows for tooltip overrides (heute bridge uses history for drawing). */
  private _chartMonths: ISalesForecastMonth[] = [];

  public constructor(
    private readonly _articlesService: ArticlesService,
    private readonly _notifyService: NotificationService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  public ngOnDestroy(): void {
    this.stopPolling();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public get displayRows(): IArticleVariantSalesForecast[] {
    const result = this.currentRun()?.result;
    if (!result) {
      return [];
    }

    if (this.groupVariants) {
      return result.aggregated ? [result.aggregated] : [];
    }

    return result.variants ?? [];
  }

  public readonly strategyDescriptions: Record<string, string> = {
    Arima: 'Klassisches ARIMA/SARIMA-Modell (Autoregression + gleitender Durchschnitt). Wählt aus einer kleinen, getesteten Modellliste – stabil und schnell (einige Sekunden).',
    Auto: 'Erweiterte automatische Auswahl: prüft zusätzlich klassische statistische Modelle (z. B. Holt-Winters, Theta, Drift) und wählt das Modell mit dem besten Backtest-Fehler. Kann etwas länger dauern als ARIMA.'
  };

  public get strategyOptions(): { value: string; label: string }[] {
    const allowed = this.options?.allowedModelStrategies ?? [];
    if (!allowed.length) {
      return [];
    }

    return allowed.map(value => ({ value, label: value }));
  }

  public get totalStock(): number {
    const rows = this.currentRun()?.result?.variants ?? [];
    return rows.reduce((sum, row) => sum + (row.currentStock ?? 0), 0);
  }

  public get isGenerating(): boolean {
    return this.isCalculating() || !!this._pendingRun;
  }

  public get needsGroupedRecalculation(): boolean {
    // New runs always include aggregated. This remains for older saved runs without it.
    // While a run is in flight the user already triggered calculate — don't nag them again.
    return (
      !this.isGenerating
      && this.groupVariants
      && !!this.currentRun()?.result
      && !this.currentRun()?.result?.aggregated
    );
  }

  /** True when the open run can fill the currently selected Pro-Variante / Gruppiert view. */
  public get hasViewableResult(): boolean {
    return this.displayRows.length > 0;
  }

  public get chartVariantChoices(): IArticleVariantSalesForecast[] {
    return this.currentRun()?.result?.variants ?? [];
  }

  public strategyTooltip(strategy: string): string {
    return this.strategyDescriptions[strategy] || strategy;
  }

  public periodLabel(months: number): string {
    if (months === 12) {
      return 'Jahr (12 Monate)';
    }
    return `${months} Monate`;
  }

  public isInsufficientHistory(row: IArticleVariantSalesForecast): boolean {
    const status = (row.status ?? '').toLowerCase();
    return status === 'insufficienthistory' || status === 'no forecast possible';
  }

  public isLowConfidence(row: IArticleVariantSalesForecast): boolean {
    return (row.status ?? '').toLowerCase() === 'lowconfidence';
  }

  public mhdTagKind(row: IArticleVariantSalesForecast): MhdTagKind {
    if (this.isInsufficientHistory(row) || !row.mhd) {
      return 'none';
    }

    if (row.mhdWarning === true) {
      return 'risk';
    }

    const monthsUntilMhd = moment(row.mhd).diff(moment(), 'months', true);
    if (monthsUntilMhd < 3) {
      return 'soon';
    }

    return 'ok';
  }

  public formatAvailableUntil(row: IArticleVariantSalesForecast): string {
    if (this.isInsufficientHistory(row)) {
      return 'keine Prognose möglich (zu wenig Historie)';
    }

    if (!row.availableUntil) {
      return '—';
    }

    return moment(row.availableUntil).format('MM/YYYY');
  }

  public formatMhdDate(mhd: string | null | undefined): string {
    if (!mhd) {
      return '';
    }

    return moment(mhd).format('DD.MM.YYYY');
  }

  public get generatedAtLabel(): string {
    const run = this.currentRun();
    const raw =
      run?.result?.generatedAt
      || run?.completedAt
      || run?.startedAt
      || run?.createdAt
      || null;
    if (!raw) {
      return '';
    }

    return moment(raw).format('DD.MM.YYYY HH:mm');
  }

  public remainingStockLine(row: IArticleVariantSalesForecast): string {
    const months = (row.monthly ?? []).filter(m => m.remainingStock != null);
    if (!months.length) {
      return '';
    }

    const parts: string[] = [];
    for (let i = 0; i < months.length; i++) {
      const m = months[i];
      // First burn-down point = stock as of today.
      const remaining = i === 0 && row.currentStock != null
        ? Math.round(row.currentStock)
        : Math.round(m.remainingStock as number);
      parts.push(`${moment(m.yearMonth, 'YYYY-MM').format('MM/YY')}: ${remaining}`);
      if (remaining <= 0) {
        break;
      }
    }

    return parts.join(' · ');
  }

  public calculate(): void {
    if (!this._articleId || this.isGenerating) {
      return;
    }

    this.stopPolling();
    this.isCalculating.set(true);
    this._cdr.markForCheck();

    this._articlesService
      .startSalesForecast(this._articleId, {
        periodMonths: this.selectedPeriodMonths,
        groupVariants: this.groupVariants,
        modelStrategy: this.selectedStrategy
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: run => this.handleRunResponse(run),
        error: err => {
          this.isCalculating.set(false);
          this._notifyService.pushError(
            'Fehler',
            err?.Message || err?.message || 'Forecast konnte nicht gestartet werden.'
          );
          this._cdr.markForCheck();
        }
      });
  }

  public onGroupVariantsChange(group: boolean): void {
    this.groupVariants = group;
    this.ensureChartSelection();
    this.refreshChart();
    this._cdr.markForCheck();
  }

  public onChartVariantChange(): void {
    this.refreshChart();
    this._cdr.markForCheck();
  }

  private loadInitial(): void {
    this.isLoading.set(true);
    this._cdr.markForCheck();

    forkJoin({
      options: this._articlesService.getSalesForecastOptions(),
      latest: this._articlesService.getLatestSalesForecast(this._articleId).pipe(
        catchError(() => of(null))
      )
    })
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading.set(false);
          this._cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ options, latest }) => {
          this.applyOptions(options);
          if (latest) {
            this.applyRun(latest);
            if (this.isActiveStatus(latest.status)) {
              this.isCalculating.set(true);
              this.startPolling(latest.id);
            }
          }
        },
        error: () => {
          this._notifyService.pushError('Fehler', 'Forecast-Optionen konnten nicht geladen werden.');
        }
      });
  }

  private applyOptions(options: IArticleSalesForecastOptions): void {
    this.options = options;
    this.selectedPeriodMonths = options.defaultPeriodMonths || 12;
    this.selectedStrategy = options.defaultModelStrategy || options.allowedModelStrategies?.[0] || 'Arima';

    const max = options.maxPeriodMonths || 24;
    const candidates = [6, 12, 24].filter(m => m <= max);
    if (!candidates.includes(this.selectedPeriodMonths) && this.selectedPeriodMonths <= max) {
      candidates.push(this.selectedPeriodMonths);
      candidates.sort((a, b) => a - b);
    }
    this.periodChoices = candidates.length ? candidates : [this.selectedPeriodMonths];
  }

  private handleRunResponse(run: IArticleSalesForecastRun): void {
    if (this.isActiveStatus(run.status)) {
      this._pendingRun = run;
      this.isCalculating.set(true);
      this.startPolling(run.id);
      return;
    }

    this._pendingRun = null;
    this.applyRun(run);

    this.isCalculating.set(false);
    if ((run.status ?? '').toLowerCase() === 'failed') {
      this._notifyService.pushError('Fehler', run.errorMessage || 'Forecast fehlgeschlagen.');
    }
    this._cdr.markForCheck();
  }

  private applyRun(run: IArticleSalesForecastRun): void {
    this.currentRun.set(run);
    this.groupVariants = !!run.groupVariants;
    this.selectedPeriodMonths = run.periodMonths || this.selectedPeriodMonths;
    if (run.modelStrategy) {
      this.selectedStrategy = run.modelStrategy;
    }
    this.ensureChartSelection();
    this.refreshChart();
    this._cdr.detectChanges();
  }

  private startPolling(forecastId: number): void {
    this.stopPolling();

    const maxTicks = 45; // ~90s at 2s interval
    let ticks = 0;

    this._pollSub = timer(0, 2000)
      .pipe(
        takeUntil(this._destroy$),
        takeWhile(() => ticks < maxTicks, true),
        switchMap(() => {
          ticks += 1;
          return this._articlesService.getSalesForecastById(this._articleId, forecastId);
        })
      )
      .subscribe({
        next: run => {
          if (this.isActiveStatus(run.status)) {
            this._pendingRun = run;

            if (ticks >= maxTicks) {
              this.isCalculating.set(false);
              this._pendingRun = null;
              this.stopPolling();
              this._notifyService.pushError(
                'Fehler',
                'Forecast dauert zu lange. Bitte später erneut prüfen.'
              );
              this._cdr.markForCheck();
            }
            return;
          }

          this._pendingRun = null;
          this.applyRun(run);

          this.isCalculating.set(false);
          this.stopPolling();

          if ((run.status ?? '').toLowerCase() === 'failed') {
            this._notifyService.pushError('Fehler', run.errorMessage || 'Forecast fehlgeschlagen.');
          }

          this._cdr.markForCheck();
        },
        error: () => {
          this.isCalculating.set(false);
          this._pendingRun = null;
          this.stopPolling();
          this._notifyService.pushError('Fehler', 'Forecast-Status konnte nicht geladen werden.');
          this._cdr.markForCheck();
        }
      });
  }

  private stopPolling(): void {
    this._pollSub?.unsubscribe();
    this._pollSub = null;
  }

  private isActiveStatus(status: string | null | undefined): boolean {
    const normalized = (status ?? '').toLowerCase();
    return normalized === 'pending' || normalized === 'running';
  }

  private ensureChartSelection(): void {
    const result = this.currentRun()?.result;
    if (!result) {
      this.selectedChartVariantId = null;
      return;
    }

    // Grouped mode without an aggregated payload: do not fall back to a variant chart —
    // that left stale Pro-Variante lines visible under the "recalculate" banner.
    if (this.groupVariants) {
      this.selectedChartVariantId = result.aggregated ? 'aggregated' : null;
      return;
    }

    const variants = result.variants ?? [];
    const stillValid = variants.some(v => v.articleVariantId === this.selectedChartVariantId);
    if (!stillValid) {
      this.selectedChartVariantId = variants[0]?.articleVariantId ?? null;
    }
  }

  private selectedForecastForChart(): IArticleVariantSalesForecast | null {
    const result = this.currentRun()?.result;
    if (!result) {
      return null;
    }

    if (this.groupVariants) {
      return result.aggregated ?? null;
    }

    if (this.selectedChartVariantId === 'aggregated') {
      return null;
    }

    return (result.variants ?? []).find(v => v.articleVariantId === this.selectedChartVariantId) ?? null;
  }

  private refreshChart(): void {
    const forecast = this.selectedForecastForChart();
    if (!forecast?.monthly?.length) {
      this._chartMonths = [];
      this.chartData = { labels: [], datasets: [] };
      return;
    }

    this._chartMonths = forecast.monthly;
    const labels = forecast.monthly.map(m => moment(m.yearMonth, 'YYYY-MM').format('MM/YY'));
    const history = forecast.monthly.map(m => (m.historyCount == null ? null : m.historyCount));

    // Forecast series uses real forecast values (no bridge onto partial-month Ist≈0).
    // Continuity: place a green point on the last history month equal to Ist, then real forecast follows.
    const forecastValues = forecast.monthly.map(m => (m.forecastCount == null ? null : m.forecastCount));
    const lower = forecast.monthly.map(m => (m.forecastLower == null ? null : m.forecastLower));
    const upper = forecast.monthly.map(m => (m.forecastUpper == null ? null : m.forecastUpper));

    const firstForecastOnly = forecastValues.findIndex((v, i) => v != null && history[i] == null);
    if (firstForecastOnly > 0 && history[firstForecastOnly - 1] != null && forecastValues[firstForecastOnly - 1] == null) {
      forecastValues[firstForecastOnly - 1] = history[firstForecastOnly - 1];
      if (lower[firstForecastOnly - 1] == null) {
        lower[firstForecastOnly - 1] = history[firstForecastOnly - 1];
      }
      if (upper[firstForecastOnly - 1] == null) {
        upper[firstForecastOnly - 1] = history[firstForecastOnly - 1];
      }
    }

    const remaining = forecast.monthly.map(m => (m.remainingStock == null ? null : m.remainingStock));
    // Orange starts at first burn-down month with aktueller Bestand.
    const firstRemainingIdx = remaining.findIndex(v => v != null);
    if (firstRemainingIdx >= 0 && forecast.currentStock != null) {
      remaining[firstRemainingIdx] = forecast.currentStock;
    }

    const datasets: ChartDataset<'line'>[] = [
      {
        label: 'Verkauf (Ist)',
        data: history,
        borderColor: '#434343',
        backgroundColor: '#434343',
        pointRadius: 2,
        spanGaps: false,
        tension: 0
      },
      {
        label: 'Konfidenz (oben)',
        data: upper,
        borderColor: 'rgba(82, 196, 26, 0.15)',
        backgroundColor: 'rgba(82, 196, 26, 0.15)',
        pointRadius: 0,
        fill: '+1',
        tension: 0,
        spanGaps: false
      },
      {
        label: 'Konfidenz (unten)',
        data: lower,
        borderColor: 'rgba(82, 196, 26, 0.15)',
        backgroundColor: 'rgba(82, 196, 26, 0.15)',
        pointRadius: 0,
        fill: false,
        tension: 0,
        spanGaps: false
      },
      {
        label: 'Forecast',
        data: forecastValues,
        borderColor: '#52c41a',
        backgroundColor: '#52c41a',
        borderDash: [6, 4],
        pointRadius: 2,
        spanGaps: false,
        tension: 0
      },
      {
        label: 'Bestand (Prognose)',
        data: remaining,
        borderColor: '#fa8c16',
        backgroundColor: '#fa8c16',
        yAxisID: 'y1',
        pointRadius: 2,
        spanGaps: false,
        tension: 0
      }
    ];

    this.chartData = { labels, datasets };
  }

  /**
   * First forecast-only month is the current (often pro-rated) month — label Restmonat in tooltip.
   * Continuity bridge on the last history month keeps showing Ist there.
   */
  private formatTooltipLabel(context: TooltipItem<'line'>): string | void {
    const value = context.parsed.y;
    if (value == null || Number.isNaN(value)) {
      return;
    }

    const label = context.dataset.label ?? '';
    const month = this._chartMonths[context.dataIndex];
    const isRestmonat =
      !!month
      && month.forecastCount != null
      && month.historyCount == null
      && this.isFirstForecastMonth(context.dataIndex);

    if (isRestmonat) {
      if (label === 'Forecast') {
        return `Forecast (Restmonat): ${month!.forecastCount}`;
      }
      if (label === 'Konfidenz (oben)' && month!.forecastUpper != null) {
        return `Konfidenz (oben): ${month!.forecastUpper}`;
      }
      if (label === 'Konfidenz (unten)' && month!.forecastLower != null) {
        return `Konfidenz (unten): ${month!.forecastLower}`;
      }
    }

    return `${label}: ${value}`;
  }

  private isFirstForecastMonth(index: number): boolean {
    for (let i = 0; i < this._chartMonths.length; i++) {
      const m = this._chartMonths[i];
      if (m?.forecastCount != null && m.historyCount == null) {
        return i === index;
      }
    }
    return false;
  }
}
